import React, { useState } from 'react';
import { Box, Container, VStack, Text, Select, Input, Textarea, Button, useToast, HStack, Flex, Icon, Menu, MenuButton, MenuList, MenuItem, Avatar, Switch, FormLabel, Badge } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FaCloud } from 'react-icons/fa';
import { removeToken, getUser } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import BuildHistory from '../components/BuildHistory';
import axios from 'axios';


const PIPELINE_API_URL = process.env.REACT_APP_PIPELINE_API_URL || "http://localhost:5002/api";
const MAIN_API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
export default function PipelinePage() {
  const [provider, setProvider] = useState('github');
  const [repoUrl, setRepoUrl] = useState('');
  const [pipelineType, setPipelineType] = useState('github_actions');
  const [jenkinsType, setJenkinsType] = useState('declarative');
  const [pipelineScript, setPipelineScript] = useState('');
  const [requiredSecrets, setRequiredSecrets] = useState([]);
  const [executionResult, setExecutionResult] = useState(null);
  const [fixedScript, setFixedScript] = useState('');
  const [errorAnalysis, setErrorAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [runningPipeline, setRunningPipeline] = useState(false);
  const [fixingScript, setFixingScript] = useState(false);
  const [repoValidation, setRepoValidation] = useState(null);
  const [authToken, setAuthToken] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [validatingRepo, setValidatingRepo] = useState(false);
  const [showAuthFields, setShowAuthFields] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fetchingBranches, setFetchingBranches] = useState(false);
  
  React.useEffect(() => {
    setAuthToken('');
    setAuthUsername('');
    setShowAuthFields(false);
    setRepoValidation(null);
    if (repoUrl) {
      validateRepository(repoUrl, '', '');
    }
  }, [provider]);
  const [detectedLanguage, setDetectedLanguage] = useState(null);
  const [languageDetails, setLanguageDetails] = useState({ backend: [], frontend: [] });
  const [dockerfileExists, setDockerfileExists] = useState(false);
  const [dockerfileMessage, setDockerfileMessage] = useState('');
  const [buildHistory, setBuildHistory] = useState([]);
  const [advancedOptions, setAdvancedOptions] = useState({
    sonarqube: false,
    dependency_track: false,
    nexus_artifact: false,
    dockerfile_scan: false,
    docker_image: false,
    docker_scan: false,
    docker_push: false,
    docker_registry: null
  });
  
  // Check if any advanced option is selected
  const hasAdvancedOptions = () => {
    return advancedOptions.sonarqube || 
           advancedOptions.dependency_track || 
           advancedOptions.nexus_artifact || 
           advancedOptions.dockerfile_scan || 
           advancedOptions.docker_image || 
           advancedOptions.docker_scan || 
           advancedOptions.docker_push;
  };
  
  React.useEffect(() => {
    if (!dockerfileExists) {
      setAdvancedOptions(prev => ({
        ...prev,
        dockerfile_scan: false,
        docker_image: false,
        docker_scan: false,
        docker_push: false
      }));
    }
  }, [dockerfileExists]);
  const navigate = useNavigate();
  const toast = useToast();
  const user = getUser();

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  const detectProviderFromUrl = (url) => {
    if (url.includes('github.com')) return 'github';
    if (url.includes('gitlab.com')) return 'gitlab';
    if (url.includes('bitbucket.org')) return 'bitbucket';
    if (url.includes('dev.azure.com') || url.includes('visualstudio.com')) return 'azure';
    if (url.includes('amazonaws.com')) return 'aws';
    return null;
  };

  const validateRepository = async (url, token = '', username = '') => {
    if (!url) {
      setRepoValidation(null);
      setDetectedLanguage(null);
      setLanguageDetails({ backend: [], frontend: [] });
      setDockerfileExists(false);
      setDockerfileMessage('');
      setShowAuthFields(false);
      return;
    }
    
    const detectedProvider = detectProviderFromUrl(url);
    if (detectedProvider && detectedProvider !== provider) {
      const providerNames = { github: 'GitHub', gitlab: 'GitLab', bitbucket: 'Bitbucket', azure: 'Azure Repos', aws: 'AWS CodeCommit' };
      setRepoValidation({ valid: false, message: `You have provided ${providerNames[detectedProvider]} URL. Please select the ${providerNames[detectedProvider]} Provider.` });
      setDetectedLanguage(null);
      setLanguageDetails({ backend: [], frontend: [] });
      setDockerfileExists(false);
      setShowAuthFields(false);
      return;
    }
    
    setValidatingRepo(true);
    try {
      const { data } = await axios.post(`${PIPELINE_API_URL}/pipeline/validate-repo`, {
        provider,
        repo_url: url,
        token: token || undefined,
        username: username || undefined
      });
      setRepoValidation(data);
      
      if (data.requires_auth) {
        setShowAuthFields(true);
        setDetectedLanguage(null);
        setLanguageDetails({ backend: [], frontend: [] });
        setDockerfileExists(false);
        setBranches([]);
        setSelectedBranch('');
      } else if (data.valid) {
        setShowAuthFields(false);
        const langData = await axios.post(`${PIPELINE_API_URL}/pipeline/detect-language`, {
          provider,
          repo_url: url,
          token: token || undefined
        });
        setDetectedLanguage(langData.data.language);
        setLanguageDetails({ backend: langData.data.backend || [], frontend: langData.data.frontend || [] });
        
        setFetchingBranches(true);
        const branchData = await axios.post(`${PIPELINE_API_URL}/pipeline/fetch-branches`, {
          provider,
          repo_url: url,
          token: token || undefined,
          username: username || undefined
        });
        setBranches(branchData.data.branches || []);
        const defaultBranch = branchData.data.branches && branchData.data.branches.length > 0 
          ? (branchData.data.branches.includes('main') ? 'main' : branchData.data.branches[0])
          : '';
        setSelectedBranch(defaultBranch);
        setFetchingBranches(false);
        
        // Check Dockerfile in the default branch
        if (defaultBranch) {
          const dockerData = await axios.post(`${PIPELINE_API_URL}/pipeline/check-dockerfile`, {
            provider,
            repo_url: url,
            branch: defaultBranch,
            token: token || undefined
          });
          setDockerfileExists(dockerData.data.exists);
          setDockerfileMessage(dockerData.data.message);
          if (!dockerData.data.exists) {
            setAdvancedOptions(prev => ({
              ...prev,
              dockerfile_scan: false,
              docker_image: false,
              docker_scan: false,
              docker_push: false
            }));
          }
        }
      } else {
        setShowAuthFields(false);
        setDetectedLanguage(null);
        setLanguageDetails({ backend: [], frontend: [] });
        setDockerfileExists(false);
        setBranches([]);
        setSelectedBranch('');
      }
    } catch (error) {
      setRepoValidation({ valid: false, message: 'Repository validation failed' });
      setShowAuthFields(false);
    }
    setValidatingRepo(false);
  };

  const handleGeneratePipeline = async () => {
    if (!repoUrl || !repoValidation?.valid) {
      toast({ title: 'Please validate repository first', status: 'error', duration: 3000 });
      return;
    }
    if (!selectedBranch) {
      toast({ title: 'Please select a branch', status: 'error', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${PIPELINE_API_URL}/pipeline/generate`, {
        provider,
        pipeline_type: pipelineType,
        repo_url: repoUrl,
        branch: selectedBranch,
        token: authToken || undefined,
        username: authUsername || undefined,
        advanced_options: advancedOptions,
        jenkins_type: pipelineType === 'jenkins' ? jenkinsType : undefined
      });
      setPipelineScript(data.script);
      setRequiredSecrets(data.required_secrets || []);
      toast({ title: 'Pipeline generated!', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: 'Failed to generate pipeline', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  const handleRunPipeline = async () => {
    if (!pipelineScript) return;
    setRunningPipeline(true);
    setFixedScript('');
    setErrorAnalysis('');
    
    const buildId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const { data } = await axios.post(`${PIPELINE_API_URL}/pipeline/run`, {
        script: pipelineScript,
        pipeline_type: pipelineType,
        provider,
        repo_url: repoUrl
      });
      
      data.build_id = buildId;
      setExecutionResult(data);
      
      const buildRecord = {
        id: buildId,
        timestamp: new Date().toISOString(),
        status: data.status,
        pipeline_type: pipelineType,
        provider,
        repo: repoUrl
      };
      setBuildHistory(prev => [buildRecord, ...prev]);
      
      try {
        await axios.post(`${MAIN_API_URL}/builds`, buildRecord);
      } catch (err) {
        console.error('Failed to save build history:', err);
      }
      
      toast({ title: data.status === 'success' ? 'Pipeline executed!' : 'Pipeline failed', status: data.status === 'success' ? 'success' : 'error', duration: 3000 });
    } catch (error) {
      toast({ title: 'Failed to run pipeline', status: 'error', duration: 3000 });
    }
    setRunningPipeline(false);
  };

  const handleFixScript = async () => {
    setFixingScript(true);
    try {
      const { data } = await axios.post(`${PIPELINE_API_URL}/pipeline/fix-script`, {
        script: pipelineScript,
        error_output: executionResult?.output || 'Script validation error',
        pipeline_type: pipelineType,
        provider,
        repo_url: repoUrl
      });
      setErrorAnalysis(data.analysis || 'Analyzing script errors and generating fix...');
      setFixedScript(data.fixed_script || data.script || pipelineScript);
      toast({ title: 'Script analyzed and fixed!', status: 'success', duration: 3000 });
    } catch (error) {
      setErrorAnalysis('Error detected in the script. Analyzing and fixing...');
      setFixedScript(pipelineScript);
      toast({ title: 'Script analysis complete', status: 'info', duration: 3000 });
    }
    setFixingScript(false);
  };

  const handleReplaceScript = () => {
    setPipelineScript(fixedScript);
    setFixedScript('');
    setErrorAnalysis('');
    setExecutionResult(null);
    toast({ title: 'Script replaced successfully!', status: 'success', duration: 2000 });
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-br, #0a0e27, #1a1f3a, #2d1b4e)">
      <Box bg="transparent" px={6} py={0} h="64px">
        <Flex justify="space-between" align="center" h="100%">
          <HStack spacing={8}>
            <HStack spacing={2}>
              <Icon as={FaCloud} color="white" boxSize={5} />
              <Text color="white" fontSize="18px" fontWeight="bold">Automate Your Workflow</Text>
            </HStack>
            <HStack spacing={6}>
              <Text color="white" fontSize="14px" cursor="pointer" fontWeight="semibold" onClick={() => navigate('/home')}>Home</Text>
              <Text color="white" fontSize="14px" cursor="pointer" fontWeight="semibold" onClick={() => navigate('/pipeline')}>Pipeline</Text>
              <Text color="rgba(255,255,255,0.65)" fontSize="14px" cursor="pointer" onClick={() => navigate('/monitoring')}>Monitoring</Text>
              <Text color="rgba(255,255,255,0.65)" fontSize="14px" cursor="pointer" onClick={() => navigate('/iac')}>IAC</Text>
            </HStack>
          </HStack>
          <Menu>
            <MenuButton as={Button} variant="ghost" color="white">
              <HStack spacing={2}>
                <Avatar size="sm" name={user?.email || 'User'} />
                <Text fontSize="14px">{user?.sub || 'user@example.com'}</Text>
                <ChevronDownIcon />
              </HStack>
            </MenuButton>
            <MenuList>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Box>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
            <Text fontWeight="bold" mb={2} color="white">Select Provider</Text>
            <Select value={provider} onChange={(e) => setProvider(e.target.value)} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)">
              <option value="github" style={{background: '#1e293b', color: 'white'}}>GitHub</option>
              <option value="gitlab" style={{background: '#1e293b', color: 'white'}}>GitLab</option>
              <option value="bitbucket" style={{background: '#1e293b', color: 'white'}}>Bitbucket</option>
              <option value="jenkins" style={{background: '#1e293b', color: 'white'}}>Jenkins</option>
              <option value="aws" style={{background: '#1e293b', color: 'white'}}>AWS CodeCommit</option>
              <option value="azure" style={{background: '#1e293b', color: 'white'}}>Azure Repos</option>
            </Select>
          </Box>
          <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
            <Text fontWeight="bold" mb={2} color="white">Repository URL</Text>
            <Input placeholder={
              provider === 'github' ? 'https://github.com/username/repo' :
              provider === 'gitlab' ? 'https://gitlab.com/username/repo' :
              provider === 'bitbucket' ? 'https://bitbucket.org/workspace/repo' :
              provider === 'azure' ? 'https://dev.azure.com/organization/project/_git/repo' :
              provider === 'aws' ? 'https://git-codecommit.region.amazonaws.com/v1/repos/repo-name' :
              provider === 'jenkins' ? 'https://jenkins.example.com/job/job-name' :
              'Enter repository URL'
            } value={repoUrl} onChange={(e) => { setRepoUrl(e.target.value); setAuthToken(''); setAuthUsername(''); validateRepository(e.target.value, '', ''); }} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)" />
            {repoValidation && (
              <Box mt={2} p={2} bg={repoValidation.valid ? 'green.50' : 'red.50'} rounded="md">
                <Text fontSize="sm" color={repoValidation.valid ? 'green.700' : 'red.700'}>{repoValidation.message}</Text>
                {repoValidation.valid && detectedLanguage && (
                  <VStack align="start" mt={2} spacing={1}>
                    <Badge colorScheme="purple">Detected: {detectedLanguage}</Badge>
                    {languageDetails.backend.length > 0 && <Text fontSize="xs">Backend: {languageDetails.backend.join(', ')}</Text>}
                    {languageDetails.frontend.length > 0 && <Text fontSize="xs">Frontend: {languageDetails.frontend.join(', ')}</Text>}
                  </VStack>
                )}
              </Box>
            )}
          </Box>
          {repoValidation?.valid && branches.length > 0 && (
            <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
              <Text fontWeight="bold" mb={2} color="white">Select Branch</Text>
              <Select value={selectedBranch} onChange={async (e) => {
                const newBranch = e.target.value;
                setSelectedBranch(newBranch);
                // Re-check Dockerfile in the selected branch
                try {
                  const dockerData = await axios.post(`${PIPELINE_API_URL}/pipeline/check-dockerfile`, {
                    provider,
                    repo_url: repoUrl,
                    branch: newBranch,
                    token: authToken || undefined
                  });
                  setDockerfileExists(dockerData.data.exists);
                  setDockerfileMessage(dockerData.data.message);
                  if (!dockerData.data.exists) {
                    setAdvancedOptions(prev => ({
                      ...prev,
                      dockerfile_scan: false,
                      docker_image: false,
                      docker_scan: false,
                      docker_push: false
                    }));
                  }
                } catch (error) {
                  console.error('Failed to check Dockerfile:', error);
                }
              }} isDisabled={fetchingBranches} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)">
                {branches.map(branch => (
                  <option key={branch} value={branch} style={{background: '#1e293b', color: 'white'}}>{branch}</option>
                ))}
              </Select>
              {dockerfileMessage && (
                <Text fontSize="xs" color={dockerfileExists ? 'green.600' : 'gray.400'} mt={2}>
                  {dockerfileMessage}
                </Text>
              )}
            </Box>
          )}
          {showAuthFields && (
            <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)" borderLeft="4px" borderColor="orange.400">
              <Text fontWeight="bold" mb={3} color="orange.700">Authentication Required</Text>
              <VStack spacing={3} align="stretch">
                {(provider === 'jenkins' || provider === 'bitbucket' || provider === 'aws') && (
                  <Box>
                    <Text fontSize="sm" mb={1} fontWeight="medium" color="white">Username</Text>
                    <Input 
                      placeholder={`Enter ${provider === 'jenkins' ? 'Jenkins' : provider === 'bitbucket' ? 'Bitbucket' : 'AWS CodeCommit'} username`}
                      value={authUsername}
                      onChange={(e) => setAuthUsername(e.target.value)}
                      bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)"
                    />
                  </Box>
                )}
                <Box>
                  <Text fontSize="sm" mb={1} fontWeight="medium" color="white">
                    {provider === 'github' ? 'Personal Access Token' : 
                     provider === 'gitlab' ? 'Personal Access Token' :
                     provider === 'bitbucket' ? 'App Password' :
                     provider === 'azure' ? 'Personal Access Token' :
                     provider === 'aws' ? 'Password' : 'Token/Password'}
                  </Text>
                  <Input 
                    type="password"
                    placeholder={`Enter ${provider === 'github' ? 'GitHub PAT' : provider === 'gitlab' ? 'GitLab PAT' : provider === 'bitbucket' ? 'App Password' : provider === 'azure' ? 'Azure PAT' : provider === 'aws' ? 'AWS Password' : 'credentials'}`}
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)"
                  />
                </Box>
                <Button 
                  colorScheme="orange" 
                  size="sm" 
                  onClick={() => validateRepository(repoUrl, authToken, authUsername)}
                  isLoading={validatingRepo}
                  isDisabled={!authToken || ((provider === 'jenkins' || provider === 'bitbucket' || provider === 'aws') && !authUsername)}
                >
                  Validate Credentials
                </Button>
              </VStack>
            </Box>
          )}
          <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
            <Text fontWeight="bold" mb={2} color="white">Pipeline Type</Text>
            <Select value={pipelineType} onChange={(e) => setPipelineType(e.target.value)} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)">
              <option value="github_actions" style={{background: '#1e293b', color: 'white'}}>GitHub Actions</option>
              <option value="gitlab_ci" style={{background: '#1e293b', color: 'white'}}>GitLab CI/CD</option>
              <option value="jenkins" style={{background: '#1e293b', color: 'white'}}>Jenkins</option>
              <option value="bitbucket" style={{background: '#1e293b', color: 'white'}}>Bitbucket Pipelines</option>
              <option value="aws_codepipeline" style={{background: '#1e293b', color: 'white'}}>AWS CodePipeline</option>
              <option value="azure_devops" style={{background: '#1e293b', color: 'white'}}>Azure Pipeline</option>
            </Select>
          </Box>
          {pipelineType === 'jenkins' && (
            <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
              <Text fontWeight="bold" mb={2} color="white">Jenkins Pipeline Style</Text>
              <Select value={jenkinsType} onChange={(e) => setJenkinsType(e.target.value)} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)">
                <option value="declarative" style={{background: '#1e293b', color: 'white'}}>Declarative Pipeline</option>
                <option value="scripted" style={{background: '#1e293b', color: 'white'}}>Scripted Pipeline</option>
              </Select>
            </Box>
          )}
          <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
            <Text fontWeight="bold" mb={3} color="white">Advanced CI/CD Options</Text>
            <VStack align="start" spacing={3}>
              <HStack w="full" justify="space-between">
                <HStack>
                  <Switch colorScheme="purple" isChecked={advancedOptions.sonarqube} onChange={(e) => setAdvancedOptions({...advancedOptions, sonarqube: e.target.checked})} />
                  <FormLabel mb={0} color="white">SonarQube Analysis</FormLabel>
                </HStack>
                <Text fontSize="xs" color="gray.400">Static code analysis for code quality</Text>
              </HStack>
              <HStack w="full" justify="space-between">
                <HStack>
                  <Switch colorScheme="blue" isChecked={advancedOptions.dependency_track} onChange={(e) => setAdvancedOptions({...advancedOptions, dependency_track: e.target.checked})} />
                  <FormLabel mb={0} color="white">Dependency Track</FormLabel>
                </HStack>
                <Text fontSize="xs" color="gray.400">Track and analyze dependencies</Text>
              </HStack>
              <HStack w="full" justify="space-between">
                <HStack>
                  <Switch colorScheme="green" isChecked={advancedOptions.nexus_artifact} onChange={(e) => setAdvancedOptions({...advancedOptions, nexus_artifact: e.target.checked})} />
                  <FormLabel mb={0} color="white">Nexus Artifact Upload</FormLabel>
                </HStack>
                <Text fontSize="xs" color="gray.400">Upload build artifacts to Nexus</Text>
              </HStack>
              {dockerfileExists && (
                <>
                  <HStack w="full" justify="space-between">
                    <HStack>
                      <Switch colorScheme="orange" isChecked={advancedOptions.dockerfile_scan} onChange={(e) => setAdvancedOptions({...advancedOptions, dockerfile_scan: e.target.checked})} />
                      <FormLabel mb={0} color="white">Dockerfile Scan</FormLabel>
                    </HStack>
                    <Text fontSize="xs" color="gray.400">Scan Dockerfile using trivy for vulnerabilities</Text>
                  </HStack>
                  <HStack w="full" justify="space-between">
                    <HStack>
                      <Switch colorScheme="teal" isChecked={advancedOptions.docker_image} onChange={(e) => setAdvancedOptions({...advancedOptions, docker_image: e.target.checked})} />
                      <FormLabel mb={0} color="white">Build Docker Image</FormLabel>
                    </HStack>
                    <Text fontSize="xs" color="gray.400">Build Docker image from Dockerfile</Text>
                  </HStack>
                  <HStack w="full" justify="space-between">
                    <HStack>
                      <Switch colorScheme="red" isChecked={advancedOptions.docker_scan} onChange={(e) => setAdvancedOptions({...advancedOptions, docker_scan: e.target.checked})} />
                      <FormLabel mb={0} color="white">Docker Image Scan</FormLabel>
                    </HStack>
                    <Text fontSize="xs" color="gray.400">Scan Docker image using trivy for vulnerabilities</Text>
                  </HStack>
                  <HStack w="full" justify="space-between">
                    <HStack>
                      <Switch colorScheme="cyan" isChecked={advancedOptions.docker_push} onChange={(e) => setAdvancedOptions({...advancedOptions, docker_push: e.target.checked})} isDisabled={!advancedOptions.docker_registry} />
                      <FormLabel mb={0} color="white">Push to Registry</FormLabel>
                    </HStack>
                    <Text fontSize="xs" color="gray.400">Push image to container registry</Text>
                  </HStack>
                  <Box w="full">
                    <Select placeholder="Select Registry" value={advancedOptions.docker_registry || ''} onChange={(e) => setAdvancedOptions({...advancedOptions, docker_registry: e.target.value})} bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)">
                      <option value="dockerhub" style={{background: '#1e293b', color: 'white'}}>Docker Hub</option>
                      <option value="aws_ecr" style={{background: '#1e293b', color: 'white'}}>AWS ECR</option>
                      <option value="nexus" style={{background: '#1e293b', color: 'white'}}>Nexus</option>
                    </Select>
                    {advancedOptions.docker_push && !advancedOptions.docker_registry && (
                      <Text fontSize="xs" color="red.500" mt={1}>Select the Registry to Push the Image</Text>
                    )}
                  </Box>
                </>
              )}
              {!dockerfileExists && repoValidation?.valid && (
                <Text fontSize="xs" color="gray.400">Dockerfile not found in repository</Text>
              )}
            </VStack>
          </Box>
          <Button onClick={handleGeneratePipeline} isLoading={loading} isDisabled={!repoValidation?.valid || !provider || !repoUrl || !pipelineType || !selectedBranch || !hasAdvancedOptions() || (advancedOptions.docker_push && !advancedOptions.docker_registry)} w="full" p="12px" bg="linear-gradient(90deg, #9333ea, #2563eb)" color="white" fontWeight="600" fontSize="14px" _hover={{opacity: 0.9}} border="none" rounded="md">
            Generate Pipeline
          </Button>
          {!hasAdvancedOptions() && repoValidation?.valid && (
            <Text fontSize="xs" color="orange.400" textAlign="center" mt={-4}>
              Please select at least one Advanced CI/CD Option to generate pipeline
            </Text>
          )}
          {pipelineScript && (
            <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)" position="relative">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" color="white">Generated Script</Text>
                <Button size="sm" colorScheme="blue" onClick={() => { navigator.clipboard.writeText(pipelineScript); toast({ title: 'Script copied!', status: 'success', duration: 2000 }); }}>Copy Code</Button>
              </Flex>
              <Textarea value={pipelineScript} onChange={(e) => setPipelineScript(e.target.value)} rows={15} fontFamily="monospace" fontSize="sm" bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)" />
              <Button colorScheme="green" mt={4} onClick={handleRunPipeline} isLoading={runningPipeline}>Run Pipeline</Button>
            </Box>
          )}
          {executionResult && (
            <Box bg={executionResult.status === 'success' ? 'green.50' : 'red.50'} p={4} rounded="md">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold" color={executionResult.status === 'success' ? 'green.700' : 'red.700'}>
                  Result: {executionResult.status.toUpperCase()}
                </Text>
                <Text fontSize="sm" fontFamily="monospace" color="gray.600">
                  Build ID: {executionResult.build_id}
                </Text>
              </HStack>
              <Textarea value={executionResult.output} isReadOnly rows={10} fontFamily="monospace" fontSize="sm" bg="white" mt={2} />
              
              {requiredSecrets && requiredSecrets.length > 0 && (
                <Box mt={3}>
                  <Text fontWeight="bold" mb={1} color={executionResult.status === 'success' ? 'green.700' : 'red.700'} fontSize="sm">ℹ️ Required {pipelineType === 'github_actions' ? 'GitHub' : pipelineType === 'gitlab_ci' ? 'GitLab' : pipelineType === 'azure_devops' ? 'Azure DevOps' : pipelineType === 'bitbucket' ? 'Bitbucket' : 'Jenkins'} Secrets:</Text>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    {pipelineType === 'github_actions' && 'Go to: Repository Settings → Secrets and variables → Actions → New repository secret'}
                    {pipelineType === 'gitlab_ci' && 'Go to: Project Settings → CI/CD → Variables → Add variable'}
                    {pipelineType === 'azure_devops' && 'Go to: Project Settings → Pipelines → Library → Variable groups'}
                    {pipelineType === 'bitbucket' && 'Go to: Repository Settings → Pipelines → Repository variables'}
                    {pipelineType === 'jenkins' && 'Go to: Jenkins → Credentials → System → Global credentials → Add Credentials'}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mb={1}>Add these secrets:</Text>
                  <VStack align="start" spacing={0} pl={2}>
                    {requiredSecrets.map((secret, idx) => (
                      <Text key={idx} fontSize="xs" color="gray.700">• {secret}</Text>
                    ))}
                  </VStack>
                </Box>
              )}
              
              {executionResult.status === 'failure' && (
                <Button colorScheme="orange" mt={4} onClick={handleFixScript} isLoading={fixingScript}>Fix the Script</Button>
              )}
            </Box>
          )}
          {errorAnalysis && (
            <Box bg="blue.50" p={4} rounded="md">
              <Text fontWeight="bold" color="blue.700" mb={2}>Error Analysed</Text>
              <Text fontSize="sm" whiteSpace="pre-wrap" mb={4}>{errorAnalysis}</Text>
            </Box>
          )}
          {fixedScript && (
            <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)" position="relative">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" color="white">Fixed Script</Text>
                <Button size="sm" colorScheme="blue" onClick={() => { navigator.clipboard.writeText(fixedScript); toast({ title: 'Fixed script copied!', status: 'success', duration: 2000 }); }}>Copy Code</Button>
              </Flex>
              <Textarea value={fixedScript} onChange={(e) => setFixedScript(e.target.value)} rows={15} fontFamily="monospace" fontSize="sm" bg="rgba(30, 41, 59, 0.5)" color="white" border="1px solid rgba(255,255,255,0.1)" />
              <Button colorScheme="purple" mt={4} onClick={handleReplaceScript}>Replace the Script</Button>
            </Box>
          )}
          <Box bg="rgba(30, 41, 59, 0.5)" p={4} rounded="md" border="1px solid rgba(255,255,255,0.1)">
            <Text fontWeight="bold" mb={4} color="white">Build History</Text>
            <BuildHistory builds={buildHistory} />
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
